import { Link } from 'react-router-dom';
import { Bot, Puzzle, Users } from 'lucide-react';
import { useGuilds } from '@/lib/hooks';
import type { GuildSummary } from '@/lib/types';
import { BOT_PLUGINS } from '@/lib/plugins';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserMention } from '@/components/UserMention';

export function ServerList() {
  const { data, isLoading, error } = useGuilds(true);

  if (isLoading) return <p className="text-muted-foreground">Lade Server…</p>;
  if (error) return <p className="text-destructive">Fehler: {(error as Error).message}</p>;

  const guilds = data?.guilds ?? [];
  if (guilds.length === 0) {
    return (
      <div className="mx-auto max-w-lg">
        <h1 className="mb-4 text-2xl font-semibold">Server</h1>
        <Card>
          <CardHeader>
            <CardTitle>Keine gemeinsamen Server</CardTitle>
            <CardDescription>
              Der Bot ist auf keinem der Server aktiv, auf denen du Mitglied bist.
              Lade den Bot ein, dann taucht er hier auf.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold">Server</h1>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {guilds.map((g) => (
          <ServerCard key={g.id} guild={g} />
        ))}
      </div>
    </div>
  );
}

function ServerCard({ guild: g }: { guild: GuildSummary }) {
  return (
    <Link to={`/server/${g.id}`}>
      <Card className="h-full transition-colors hover:border-primary">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <GuildIcon name={g.name} iconUrl={g.iconUrl} />
            <div className="min-w-0">
              <CardTitle className="truncate text-lg">{g.name}</CardTitle>
              <CardDescription className="truncate">
                Owner: {g.ownerId ? <UserMention id={g.ownerId} showAvatar={false} /> : '—'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              {g.memberCount ?? '—'} Mitglieder
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Bot className="h-4 w-4" />
              {g.botCount ?? '—'} {g.botCount === 1 ? 'Bot' : 'Bots'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Puzzle className="h-4 w-4" />
              {g.activePluginCount} / {BOT_PLUGINS.length} Plugins aktiv
            </span>
          </div>
          <div className="border-t pt-3 text-xs text-muted-foreground">
            <span className="mr-1">Dashboard-Admins:</span>
            {g.dashboardAdmins.length === 0
              ? '—'
              : g.dashboardAdmins.map((a, i) => (
                  <span key={a.discordId}>
                    {i > 0 && ', '}
                    <UserMention id={a.discordId} showAvatar={false} />
                  </span>
                ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/** Discord-style fallback: initials on a coloured disc when no icon is set. */
function GuildIcon({ name, iconUrl }: { name: string; iconUrl: string | null }) {
  if (iconUrl) return <img src={iconUrl} alt="" className="h-11 w-11 shrink-0 rounded-full" />;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
      {initials || '?'}
    </div>
  );
}
